import React, { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';

// Third party components
import {Box, CardContent, Grid, Chip, FormHelperText, InputLabel, Stack, Typography} from '@mui/material';
import GeoJSON from 'ol/format/GeoJSON';

// Generic components
import {
  Button, LineLoader, LoadingSpinner, FormErrorMessage, ContainerWithCard,
  RequiredIndicator, SubHeader, AnyField
} from '@timmons-group/shared-react-components';

import ThemeMap from 'components/ThemeMap';

// Hooks
import {
  useStaleData, useMapConfig, useQuery
} from '@timmons-group/shared-react-components/hooks';

// TODO: Get this to shared-react-components once DB is updated
import { useLayout } from 'hooks/useData';

import { ZOOMABLES_URL, COMMUNITY_TYPE_LAYERS } from 'constants.js';
import {FIELD_TYPES} from '@timmons-group/shared-react-components/constants';
import { getSectionChoices } from 'helpers.js';
import { useNavigate } from 'react-router-dom';

const NewCommunityArea = () => {
  const [zoomables, zoomablesLoading] = useStaleData(ZOOMABLES_URL);
  const [layout, layoutLoading] = useLayout('community', 'newModal');
  const [stateExtent, setStateExtent] = useState(null);
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [activeLayers, setActiveLayers] = useState([]);
  const [communityType, setCommunityType] = useState('Community Type Loading');
  const [features, setFeatures] = useState([]);
  const [saving, setSaving] = useState(false);
  const query = useQuery();
  const nav = useNavigate();

  const qState = query.get('state');
  const qType = query.get('type');
  const olMap = useRef(null);
  const oThemeMap = useRef(null);

  const [mapConfig, mapConfigLoading] = useMapConfig('new_planning_area');

  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: '',
    }
  });

  // useMemo is used to prevent the refLayers / zoomables from being re-rendered every time
  useMemo(() => {
    if (!mapConfigLoading && !layoutLoading) {
      //Pluck out the community type from the layout
      const choices = getSectionChoices(layout, 'New Community', 'communityType');
      setCommunityType(choices?.find(item => item.id == qType)?.label || 'Community Type Not Found');

      //Find the zoomable layer for the communityType...eventually.
      // TODO: FIX THIS hardcoding when we have a way to map communityType to layer id
      const found = mapConfig?.layers?.find(item => item.key == COMMUNITY_TYPE_LAYERS[parseInt(qType)]);
      setActiveLayers(found ? [found.key] : []);

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapConfigLoading, layoutLoading]);

  const state = useMemo(() => {
    const found = zoomables?.length ? zoomables.find(item => item.id == qState) : null;
    setStateExtent(found?.fields);
    return found?.name || 'State Not Found';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomablesLoading]);

  const areasInvalid = useMemo(() => {
    return (submitAttempt && features.length < 1);
  }, [submitAttempt, features.length]);

  const onSubmit = async (data) => {
    if (features.length) {
      setSaving(true);
      data.geometry = [...features];
      data.communityType = parseInt(qType);
      data.state = parseInt(qState);

      const result = await axios.post('/api/community/new', data);
      if (result?.data?.streamID) {
        nav(`/pam/communityArea/${result.data.streamID}`);
      } else {
        setSaving(false);
      }
    }
  }

  // onSubmit is not called if the form is invalid
  // so we need to manually check for this
  const preSubmit = (evt) => {
    handleSubmit(onSubmit)(evt);
    setSubmitAttempt(true);
  }

  /**
   * Process the features from the map and add / remove them from the features state
   * @param {Array} feats - Array of features from the map
   */
  const processFeatures = (feats) => {
    // Use the previous state to determine if we are adding or removing
    setFeatures((previous) => {
      const add = [];
      const remove = [];
      feats.forEach(feature => {
        const removal = previous.findIndex(item => item.id === feature.id);
        if (removal >= 0) {
          remove.push(removal);
        } else {
          add.push(feature);
        }
      });

      const next = previous.filter((_value, index) => {
        return remove.indexOf(index) == -1;
      });

      return next.concat(add);
    });
  };

  const handleDelete = (chipToDelete) => () => {
    processFeatures([chipToDelete]);
  }

  const handleClick = (zoomToMe) => () => {
    const feats = new GeoJSON().readFeatures(zoomToMe);
    const polygon = feats && feats[0] ? feats[0].getGeometry() : null;

    if (polygon) {
      olMap.current.getView().fit(polygon);
    }
  };

  const setMap = (olm) => {
    oThemeMap.current = olm;
    // olMap is a ref to the map. So we need to get the .current property
    olMap.current = olm.olMap.current;
  };

  const theForm = () => {
    return (
      <>
        <form>
          {/* AnyField needs to be passed the control from reactHookForm */}
          <AnyField
            layout={{
              label: 'Name',
              placeholder: 'Enter a name',
              id: 'name',
              name: 'name',
              type: FIELD_TYPES.TEXT
            }}
            rules={{
              required: 'Name is required',
              maxLength: {
                value: 100,
                message: 'Name must be 100 characters or less'
              }
            }}
            control={control} />
          <InputLabel sx={{marginTop: '8px'}} error={areasInvalid}><RequiredIndicator /><span>Area Shapes</span></InputLabel>
          <FormHelperText>Click on one or more areas that make up the new Community area. Click a selected shape again to deselect it.</FormHelperText>
        </form>
        <Box sx={(theme) => {
          return {...theme.communityShapeBox, height: '100%'} || {};
        }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {features.map((item, index) => (
              <Chip
                sx={(theme) => theme?.communityChip || {}}
                key={`${item.id}_${index}`}
                label={item.properties?.name || item.id}
                onClick={handleClick(item)}
                onDelete={handleDelete(item)}
              />
            ))}
          </Stack>
          {features.length < 1 && <>Please select one or more areas.</>}
          {(areasInvalid) && <FormErrorMessage error={{ message: 'You must select at least one area' }} />}
        </Box>
      </>
    );
  }

  const rendered = () => {
    if (zoomablesLoading || mapConfigLoading || !stateExtent || layoutLoading) {
      return (
        <ContainerWithCard>
          <LineLoader message="Loading..." />
        </ContainerWithCard>
      )
    }

    return (
      <>
        <LoadingSpinner isActive={saving} />
        <SubHeader title={`Create Community Area: ${state}`}
          rightRender={
            () =>
              <Stack spacing={2} direction="row" justifyContent="flex-end">
                <Button size="small" color="error" href="/communities" label="Cancel" />
                <Button size="small" color="secondary" onClick={preSubmit} label="Save" />
              </Stack>
          }
        />
        <Grid spacing={2} container sx={(theme) => theme.communityMap || {}} justify="space-between" alignItems="stretch">
          <Grid item sx={{
            background: 'white',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            minWidth: {
              sm: '100%',
              md: '25%',
            },
            maxWidth: {
              sm: '100%',
              md: '25%',
            }
          }} >
            <Typography variant="sectionHeader">Community Details</Typography>
            <Box sx={{background: 'black', height: '1px', width: '100%', marginTop: '8px', marginBottom: '8px'}}></Box>
            {theForm()}
          </Grid>
          <Grid item sx={{
            paddingTop: {
              sm: '16px !important',
              md: '0px !important',
            },
            display: 'flex',
            flexDirection: 'column',
            minWidth: {
              sm: '100%',
              md: '75%',
            },
            maxWidth: {
              sm: '100%',
              md: '75%',
            }
          }}>
            <CardContent sx={{background:'white'}}>
              <Typography variant="sectionHeader">Map Community Area (by {communityType})</Typography>
            </CardContent>
            <ThemeMap
              defaultExtent={stateExtent?.extent}
              themes={mapConfig}
              activeLayers={activeLayers}
              featuresClicked={processFeatures}
              features={features}
              mapSet={setMap}
            />
          </Grid>
        </Grid>
      </>
    );
  }

  return rendered();
}

export default NewCommunityArea;